import apiClient, { IresponseData, TOKEN_KEY } from "@/lib/api";

export interface LoginCredentials {
  accountCode: string;
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  user: {
    accountCode: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface User {
  id: string;
  // username: string;
  accountCode: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  // role?: string;
}

// Constante para los datos del usuario en localStorage
const USER_DATA_KEY = "user_data";

/**
 * Servicio de autenticación
 */
export const authService = {
  /**
   * Iniciar sesión con credenciales
   * Guarda el token y los datos del usuario en localStorage
   */
  async login(
    credentials: LoginCredentials,
  ): Promise<IresponseData<LoginResponse>> {
    try {
      const response = await apiClient.post<IresponseData<LoginResponse>>(
        "/auth/login",
        credentials,
      );

      // Guardar el token en localStorage
      if (response.data.data?.accessToken) {
        localStorage.setItem(TOKEN_KEY, response.data.data.accessToken);
      }

      // Guardar los datos del usuario en localStorage
      if (response.data.data?.user) {
        localStorage.setItem(
          USER_DATA_KEY,
          JSON.stringify(response.data.data.user),
        );
      }

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al iniciar sesión. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Cerrar sesión
   * Limpia el token y datos del usuario de localStorage
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Error durante el logout:", error);
      // Continuar con el logout del lado del cliente incluso si falla el servidor
    } finally {
      // Limpiar localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
    }
  },

  /**
   * Verificar si el usuario está autenticado
   * Primero verifica localStorage, luego valida con el servidor
   */
  async checkAuth(): Promise<{
    id: string;
    // username: string;
    accountCode: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    // role?: string;
    account: {
      accountCode: string;
      nameAccount: string;
    };
  } | null> {
    try {
      // Verificar si hay token en localStorage
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        return null;
      }

      // Verificar con el servidor (el token se envía automáticamente en el interceptor)
      const response = await apiClient.post<
        IresponseData<{
          id: string;
          // username: string;
          accountCode: string;
          email?: string;
          firstName?: string;
          lastName?: string;
          // role?: string;
          account: {
            accountCode: string;
            nameAccount: string;
          };
        }>
      >("/auth/me");
      return response.data.data;
    } catch (error) {
      // Si falla la verificación, limpiar localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      return null;
    }
  },

  /**
   * Obtener datos del usuario desde localStorage
   */
  getUserFromStorage(): User | null {
    try {
      const userData = localStorage.getItem(USER_DATA_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error al leer usuario de localStorage:", error);
      return null;
    }
  },

  /**
   * Obtener token desde localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Verificar si hay un token válido (solo verifica existencia)
   */
  hasToken(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Refrescar el token
   * Actualiza el token en localStorage
   */
  async refreshToken(): Promise<boolean> {
    try {
      const response =
        await apiClient.post<IresponseData<{ accessToken: string }>>(
          "/auth/refresh",
        );

      // Actualizar el token en localStorage
      if (response.data.data?.accessToken) {
        localStorage.setItem(TOKEN_KEY, response.data.data.accessToken);
        return true;
      }

      return false;
    } catch (error) {
      // Si falla el refresh, limpiar localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      return false;
    }
  },
};
